import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColumns, GridRow as DataGridRow, GridSortModel } from '@mui/x-data-grid';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaTimes } from 'react-icons/fa';
import Modal from 'react-modal';
import { format, parseISO, isValid } from 'date-fns';
import { getEuImportHistoryMeasures, getEuImportHistory } from '../../../server/server';
import DateSearchHeader from '../../../common/DateSearchHeader';

interface ImportBatchMeasuresProps {
  batch: any;
  onBack: () => void;
}

function ImportBatchMeasures({ batch, onBack }: ImportBatchMeasuresProps) {
  const [tableRows, setTableRows] = useState<any[]>([]);
  const [totalTableRows, setTotalTableRows] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'id', sort: 'asc' },
  ]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [batchDetails, setBatchDetails] = useState<any>(batch);
  const tableRef = useRef<HTMLDivElement>(null);

  // Search/filter state
  const [searchFilters, setSearchFilters] = useState({
    id: '',
    recordId: '',
    celexNo: '',
    officialJournal: '',
    descriptionEN: '',
    pages: '',
    dateOfPublication: '',
    dateOfImport: '',
    dateOfLastUpdate: '',
  });

  // Fetch batch details if only id is available (e.g. from URL deep-link)
  useEffect(() => {
    if (batch.date !== undefined) {
      setBatchDetails(batch);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await getEuImportHistory(1, 0, 'date', 'DESC');
        if (resp?.status === 200 && !cancelled) {
          const found = resp.data.rows?.find((r: any) => r.id === batch.id);
          if (found) setBatchDetails(found);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [batch]);

  const openDetailModal = (row: any) => {
    setSelectedRow(row);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedRow(null);
  };

  const buildSearchParams = (): Record<string, string> => {
    const params: Record<string, string> = {};
    Object.entries(searchFilters).forEach(([key, value]) => {
      if (value && value.trim() !== '') {
        params[key] = value.trim();
      }
    });
    return params;
  };

  const loadMeasures = useCallback(async (
    offset: number = 0,
    limit: number = 10,
    sort: string = 'id',
    order: string = 'ASC',
  ) => {
    setIsLoading(true);
    try {
      const resp = await getEuImportHistoryMeasures(
        batch.id, limit, offset, sort, order, buildSearchParams(),
      );
      if (resp?.status === 200) {
        setTableRows(resp.data.rows);
        setTotalTableRows(resp.data.totalRows);
      }
    } finally {
      setIsLoading(false);
    }
  }, [batch.id, searchFilters]);

  useEffect(() => {
    loadMeasures();
  }, []);

  // Debounced auto-search for text fields
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = (field: string, value: string) => {
    setSearchFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        setPage(0);
        const sortField = sortModel[0]?.field ?? 'id';
        const sortOrder = sortModel[0]?.sort?.toUpperCase() ?? 'ASC';
        const params: Record<string, string> = {};
        Object.entries(next).forEach(([k, v]) => {
          if (v && v.trim() !== '') params[k] = v.trim();
        });
        setIsLoading(true);
        getEuImportHistoryMeasures(batch.id, pageSize, 0, sortField, sortOrder, params).then((resp) => {
          if (resp?.status === 200) {
            setTableRows(resp.data.rows);
            setTotalTableRows(resp.data.totalRows);
            setIsLoading(false);
          }
        });
      }, 300);
      return next;
    });
  };

  const handleDateSearch = (field: string, dateStr: string) => {
    setSearchFilters((prev) => {
      const next = { ...prev, [field]: dateStr };
      setPage(0);
      const sortField = sortModel[0]?.field ?? 'id';
      const sortOrder = sortModel[0]?.sort?.toUpperCase() ?? 'ASC';
      const params: Record<string, string> = {};
      Object.entries(next).forEach(([k, v]) => {
        if (v && v.trim() !== '') params[k] = v.trim();
      });
      setIsLoading(true);
      getEuImportHistoryMeasures(batch.id, pageSize, 0, sortField, sortOrder, params).then((resp) => {
        if (resp?.status === 200) {
          setTableRows(resp.data.rows);
          setTotalTableRows(resp.data.totalRows);
          setIsLoading(false);
        }
      });
      return next;
    });
  };

  const renderSearchHeader = (title: string, field: string, type: 'text' | 'date' = 'text') => {
    if (type === 'date') {
      return (
        <DateSearchHeader
          title={title}
          value={searchFilters[field as keyof typeof searchFilters] ?? ''}
          onChange={(dateStr) => handleDateSearch(field, dateStr)}
        />
      );
    }
    return (
      <div className="flex flex-col w-full" style={{ minWidth: 0 }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <span className="text-xs font-semibold mb-1">{title}</span>
        <TextField
          size="small"
          fullWidth
          value={searchFilters[field as keyof typeof searchFilters] ?? ''}
          onChange={(e) => handleSearchChange(field, e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder="Претрага..."
          variant="standard"
          sx={{
            width: '100%',
            '& .MuiInput-root': { width: '100%' },
            '& .MuiInput-input': { fontSize: '0.75rem', padding: '2px 0' },
            '& .MuiInput-underline:before': { borderBottomColor: '#81848f3d' },
            '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#1D4B8E' },
            '& .MuiInput-underline:after': { borderBottomColor: '#1D4B8E' },
          }}
        />
      </div>
    );
  };

  const handleSortModelChange = (newModel: GridSortModel) => {
    if (newModel.length === 0) {
      const prevField = sortModel[0]?.field ?? 'id';
      const prevSort: 'asc' | 'desc' = sortModel[0]?.sort === 'asc' ? 'desc' : 'asc';
      const toggled = [{ field: prevField, sort: prevSort }];
      setSortModel(toggled);
      loadMeasures(page * pageSize, pageSize, prevField, prevSort.toUpperCase());
      return;
    }
    setSortModel(newModel);
    const { field, sort } = newModel[0];
    loadMeasures(page * pageSize, pageSize, field, sort?.toUpperCase() ?? 'ASC');
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    const sortField = sortModel[0]?.field ?? 'id';
    const sortOrder = sortModel[0]?.sort?.toUpperCase() ?? 'ASC';
    loadMeasures(newPage * pageSize, pageSize, sortField, sortOrder);
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
    const sortField = sortModel[0]?.field ?? 'id';
    const sortOrder = sortModel[0]?.sort?.toUpperCase() ?? 'ASC';
    loadMeasures(0, newPageSize, sortField, sortOrder);
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const columns: GridColumns<any> = [
    {
      field: 'id',
      headerName: '',
      minWidth: 100,
      flex: 0.2,
      sortable: true,
      renderHeader: () => renderSearchHeader('ID', 'id'),
      renderCell: (params) => (params.value == null || String(params.value).trim() === '') ? '/' : params.value,
    },
    {
      field: 'recordId',
      headerName: '',
      minWidth: 120,
      flex: 0.4,
      sortable: true,
      renderHeader: () => renderSearchHeader('Шифра записа', 'recordId'),
      renderCell: (params) => (params.value == null || String(params.value).trim() === '') ? '/' : params.value,
    },
    {
      field: 'celexNo',
      headerName: '',
      minWidth: 60,
      flex: 0.4,
      sortable: true,
      renderHeader: () => renderSearchHeader('CELEX', 'celexNo'),
      renderCell: (params) => (params.value == null || String(params.value).trim() === '') ? '/' : params.value,
    },
    {
      field: 'descriptionEN',
      headerName: '',
      minWidth: 150,
      flex: 1.3,
      sortable: true,
      renderHeader: () => renderSearchHeader('Опис', 'descriptionEN'),
      renderCell: (params) => (
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {(params.value == null || String(params.value).trim() === '') ? '/' : params.value}
        </div>
      ),
    },
    {
      field: 'officialJournal',
      headerName: '',
      minWidth: 120,
      flex: 0.8,
      sortable: true,
      renderHeader: () => renderSearchHeader('Службени лист ЕУ', 'officialJournal'),
      renderCell: (params) => (
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {(params.value == null || String(params.value).trim() === '') ? '/' : params.value}
        </div>
      ),
    },
    {
      field: 'pages',
      headerName: '',
      minWidth: 80,
      flex: 0.2,
      sortable: true,
      renderHeader: () => renderSearchHeader('Странице', 'pages'),
      renderCell: (params) => (params.value == null || String(params.value).trim() === '') ? '/' : params.value,
    },
    {
      field: 'dateOfPublication',
      headerName: '',
      minWidth: 100,
      flex: 0.65,
      sortable: true,
      renderHeader: () => renderSearchHeader('Датум објаве', 'dateOfPublication', 'date'),
      renderCell: (params) => {
        if (!params.value || String(params.value).trim() === '') return '/';
        const d = parseISO(params.value);
        return isValid(d) ? format(d, 'dd.MM.yyyy.') : '/';
      },
    },
    {
      field: 'dateOfImport',
      headerName: '',
      minWidth: 100,
      flex: 0.65,
      sortable: true,
      renderHeader: () => renderSearchHeader('Датум увоза', 'dateOfImport', 'date'),
      renderCell: (params) => {
        if (!params.value || String(params.value).trim() === '') return '/';
        const d = parseISO(params.value);
        return isValid(d) ? format(d, 'dd.MM.yyyy.') : '/';
      },
    },
    {
      field: 'dateOfLastUpdate',
      headerName: '',
      minWidth: 100,
      flex: 0.65,
      sortable: true,
      renderHeader: () => renderSearchHeader('Датум измене', 'dateOfLastUpdate', 'date'),
      renderCell: (params) => {
        if (!params.value || String(params.value).trim() === '') return '/';
        const d = parseISO(params.value);
        return isValid(d) ? format(d, 'dd.MM.yyyy.') : '/';
      },
    },
  ];

  function CustomGridFooter(props: any) {
    const totalPages = Math.ceil(props.count / props.pageSize);
    const handlePageSizeChange = (event: any) => {
      props.onPageSizeChange(event.target.value);
    };
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px',
        }}
      >
        <FormControl>
          <InputLabel htmlFor="measures-rows-per-page">Редова по страници</InputLabel>
          <Select
            value={props.pageSize}
            size="small"
            label="Редова по страници"
            sx={{ minWidth: 170 }}
            onChange={handlePageSizeChange}
            inputProps={{
              name: 'measures-rows-per-page',
              id: 'measures-rows-per-page',
            }}
          >
            {props.rowsPerPageOptions.map((option: any) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <p className="text-[#04438b]">Укупно редова: {props.count}</p>
        <Pagination
          shape="rounded"
          count={totalPages}
          page={props.page + 1}
          onChange={(event, newPage) => props.onPageChange(event, newPage - 1)}
          siblingCount={1}
          boundaryCount={1}
        />
      </div>
    );
  }

  return (
    <div ref={tableRef}>
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-[#04438b] hover:text-[#1D4B8E] font-medium cursor-pointer transition-colors"
        >
          ← Назад на историјат увоза
        </button>
      </div>

      <div className="rounded-lg border border-[#81848f3d] p-4 mb-5 bg-[#f8f9fc]">
        <h2 className="text-[#04438b] text-xl font-bold mb-3">
          Детаљи увоза #{batchDetails?.id ?? batch.id}
        </h2>
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
          <div>
            <span className="text-[#60626a]">Датум: </span>
            <span className="font-medium text-[#303135]">
              {batchDetails?.date
                ? (isValid(parseISO(batchDetails.date)) ? format(parseISO(batchDetails.date), 'dd.MM.yyyy. HH:mm') : '/')
                : '/'}
            </span>
          </div>
          <div>
            <span className="text-[#60626a]">Група: </span>
            <span className="font-medium text-[#303135]">{batchDetails?.groupSetName || '/'}</span>
          </div>
          <div>
            <span className="text-[#60626a]">Корисник: </span>
            <span className="font-medium text-[#303135]">{batchDetails?.startedBy || '/'}</span>
          </div>
          <div>
            <span className="text-[#60626a]">Додато: </span>
            <span className="font-medium text-green-700">{(batchDetails?.addedRows ?? 0).toLocaleString()}</span>
          </div>
          <div>
            <span className="text-[#60626a]">Измењено: </span>
            <span className="font-medium text-blue-700">{(batchDetails?.updatedRows ?? 0).toLocaleString()}</span>
          </div>
        </div>
      </div>

      <DataGrid
        rows={tableRows}
        columns={columns}
        loading={isLoading}
        rowCount={totalTableRows}
        pagination
        paginationMode="server"
        sortingMode="server"
        sortingOrder={['asc', 'desc']}
        page={page}
        pageSize={pageSize}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        rowsPerPageOptions={[5, 10, 20, 50, 100]}
        style={{ zIndex: 0 }}
        disableColumnFilter
        disableColumnMenu
        disableSelectionOnClick
        rowHeight={60}
        autoHeight={true}
        sx={{
          '& .MuiDataGrid-columnHeader': {
            overflow: 'visible',
          },
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            overflow: 'visible',
            width: '100%',
          },
          '& .MuiDataGrid-columnHeaderWrapper': {
            overflow: 'visible',
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            paddingTop: '8px',
            paddingBottom: '8px',
            minHeight: '60px !important',
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
            transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
            borderLeft: '3px solid transparent',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(4, 67, 139, 0.18) !important',
            borderLeft: '3px solid #1D4B8E',
          },
          '& .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor: 'rgba(4, 67, 139, 0.18) !important',
            borderLeft: '3px solid #1D4B8E',
          },
        }}
        getRowId={(row) => row.id}
        onRowClick={(params) => openDetailModal(params.row)}
        components={{
          Row: (props: any) => (
            <Tooltip title="Кликните за детаље" placement="left" arrow followCursor>
              <span style={{ display: 'contents' }}>
                {/* @ts-ignore */}
                <DataGridRow {...props} />
              </span>
            </Tooltip>
          ),
          Footer: CustomGridFooter,
          NoRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              Не постоје ЕУ прописи
            </Stack>
          ),
          NoResultsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              Не постоје ЕУ прописи
            </Stack>
          ),
        }}
        componentsProps={{
          footer: {
            count: totalTableRows,
            page: page,
            pageSize: pageSize,
            onPageChange: (event: any, newPage: any) => handlePageChange(newPage),
            onPageSizeChange: (newPageSize: any) => handlePageSizeChange(newPageSize),
            rowsPerPageOptions: [5, 10, 20, 50, 100],
          },
        }}
      />

      <Modal
        isOpen={detailModalOpen}
        onRequestClose={closeDetailModal}
        style={{
          overlay: { zIndex: 1500, backgroundColor: 'rgba(0,0,0,0.5)' },
          content: {
            maxWidth: 700,
            width: '90%',
            minHeight: '85vh',
            maxHeight: '90vh',
            margin: 'auto',
            padding: 0,
            border: 'none',
            borderRadius: 12,
            boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
            overflow: 'auto',
            top: '5%',
          },
        }}
      >
        {selectedRow && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[#04438b] text-2xl font-bold">
                Детаљи ЕУ прописа
              </h2>
              <button
                onClick={closeDetailModal}
                className="text-[#60626a] hover:text-[#303135] text-xl cursor-pointer"
              >
                <FaTimes />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">ID</span>
                <span className="text-[#303135]">{selectedRow.id}</span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">Шифра записа</span>
                <span className="text-[#303135]">{selectedRow.recordId}</span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">CELEX</span>
                <span className="text-[#303135] font-mono">{selectedRow.celexNo}</span>
              </div>
              <div className="border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium block mb-1">Опис</span>
                <span className="text-[#303135]">{selectedRow.descriptionEN}</span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">Службени лист ЕУ</span>
                <span className="text-[#303135]">{selectedRow.officialJournal}</span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">Странице</span>
                <span className="text-[#303135]">{selectedRow.pages}</span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">Датум објаве</span>
                <span className="text-[#303135]">
                  {selectedRow.dateOfPublication
                    ? (isValid(parseISO(selectedRow.dateOfPublication))
                      ? format(parseISO(selectedRow.dateOfPublication), 'dd.MM.yyyy.')
                      : '/')
                    : '/'}
                </span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">Истек ваљаности</span>
                <span className="text-[#303135]">
                  {selectedRow.endOfValidityDate
                    ? (isValid(parseISO(selectedRow.endOfValidityDate))
                      ? format(parseISO(selectedRow.endOfValidityDate), 'dd.MM.yyyy.')
                      : '/')
                    : '/'}
                </span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">Статус измене</span>
                <span className="text-[#303135]">{selectedRow.editStat ?? '/'}</span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">Датум увоза</span>
                <span className="text-[#303135]">
                  {selectedRow.dateOfImport
                    ? (isValid(parseISO(selectedRow.dateOfImport))
                      ? format(parseISO(selectedRow.dateOfImport), 'dd.MM.yyyy.')
                      : '/')
                    : '/'}
                </span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">Датум измене</span>
                <span className="text-[#303135]">
                  {selectedRow.dateOfLastUpdate
                    ? (isValid(parseISO(selectedRow.dateOfLastUpdate))
                      ? format(parseISO(selectedRow.dateOfLastUpdate), 'dd.MM.yyyy.')
                      : '/')
                    : '/'}
                </span>
              </div>
              <div>
                <span className="text-[#60626a] font-medium block mb-1">Поглавља</span>
                {selectedRow.chapters && selectedRow.chapters.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedRow.chapters.map((ch: any, idx: number) => (
                      <span
                        key={idx}
                        className="bg-[#e8edf5] text-[#04438b] px-3 py-1 rounded-full text-sm"
                      >
                        {ch.nameEN} ({ch.groupNo})
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-[#303135]">/</span>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default ImportBatchMeasures;
